import React, { Fragment, useEffect, useState } from "react";

// ** Mui Components Imports ** //
import {
  Box,
  createTheme,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import NavbarComponent from "../navbar";
import {
  adminRoutes,
  privateAdminRoutes,
  privateCustomerRoute,
} from "../../navigation/navigate";
import MenuIcon from "@mui/icons-material/Menu";
import { jwtDecode } from "jwt-decode";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import logo from "../../assets/icons/logo.svg"
import "../../styles/layout/sidebar/_sidebar.scss"
import { useTranslation } from "react-i18next";
import { getClientList } from "../../services/admin-services/clientServices";
import { useCommonContext } from "../../context/common";

const drawerWidth = 268;

const Sidebar = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeItem, setActiveItem] = useState("");
  const [menu, setMenu] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectClient, setSelectClient] = useState('');
  const { clientId, setClientId } = useCommonContext();
  const selectedClient = JSON.parse(localStorage?.getItem('selectClientId'));

  const handleToggle = (index) => {
    setActiveMenu(activeMenu === index ? null : index);
  };

  const getAllClientRequestList = async (payload) => {
    const response = await getClientList(payload);
    if (response?.isSuccess) {
      setClients(response?.client)
    }
  }

  useEffect(() => {
    if (window.location.pathname) {
      setActiveItem(window.location.pathname);
    }
  }, [window.location.pathname]);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  useEffect(() => {
    const decodedToken = jwtDecode(
      JSON.parse(localStorage.getItem("authToken"))
    );
    if (decodedToken.isAdmin) {
      getAllClientRequestList();
      setIsAdmin(decodedToken.isAdmin);
      setMenu(privateAdminRoutes);
    } else {
      setMenu(privateCustomerRoute);
    }
  }, []);

  const handleClientSelect = (e) => {
    const { value } = e.target;
    if (value) {
      setSelectClient(value);
      localStorage.setItem('selectClientId', JSON.stringify(value));
    } else {
      setSelectClient("");
      localStorage.removeItem('selectClientId');
    }
  }

  useEffect(() => {
    if (isAdmin) {
      setSelectClient(selectedClient ? selectedClient : "");
      setClientId(selectedClient ? selectedClient : "")
    }
  }, [selectedClient, selectClient, isAdmin])


  const drawer = (
    <List sx={{ paddingTop: "1rem" }}>
      {menu.map((item, index) => (
        <Fragment>
          {!item.isNotNavmanu && (
            <ListItem key={index} disablePadding sx={{ width: "100%" }}>
              <NavLink
                to={item.to ? item.to : null}
                className={({ isActive }) =>
                  `sidebar-menu w-100 ${isActive ? "active" : ""}`
                }
                onClick={(e) => {
                  if (item.children?.length) {
                    e.preventDefault();
                  }
                }}
              >
                <ListItemButton
                  onClick={isAdmin ? undefined : () => handleToggle(index)}
                  sx={{
                    padding: "0 20px",
                    color: "#000",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "transparent",
                    },
                  }}
                >
                  <ListItemIcon className="menu-icon">
                    <img src={item.icon} alt="menu icon" />
                  </ListItemIcon>
                  <ListItemText
                    className="active-menu"
                    primary={
                      <Typography
                        style={{
                          whiteSpace: "nowrap",
                          fontSize: "16px",
                          fontWeight: "500",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "flex",
                          color: `${activeItem?.includes(item.to) ? "#fff" : "#000"}`,
                          background: "none",
                          fontFamily: "Poppins",
                        }}
                      >
                        {t(item.translationKey)}
                        {item.children && (
                          <ExpandMoreIcon sx={{ marginLeft: "auto" }} />
                        )}
                      </Typography>
                    }
                    sx={{
                      color: `${activeItem?.includes(item.to) ? "#fff" : "#000"}`,
                      fontFamily: "Poppins",
                    }}
                  />
                </ListItemButton>
              </NavLink>
            </ListItem>
          )}

          {item.children && (
            <div
              className={`children-container ${activeMenu === index ? "expanded" : ""}`}
              style={{
                maxHeight: activeMenu === index ? `${item.children.length * 40}px` : "0",
                overflow: "hidden",
                transition: "max-height 0.3s ease-in-out",
              }}
            >
              <List sx={{ paddingLeft: 0 }} className="children-menu">
                {item.children.filter(child => !child.isNotNavmanu).map((child, childIndex) => (
                  <ListItem key={childIndex} disablePadding>
                    <NavLink
                      to={child.to}
                      className={({ isActive }) =>
                        `sidebar-submenu ${isActive ? "child-active" : "child-inActive"}`
                      }
                    >
                      <ListItemButton
                        sx={{
                          padding: "0 20px",
                          color: "#000",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          margin: 0
                        }}
                      >
                        <ListItemIcon className="menu-icon"></ListItemIcon>
                        <ListItemText
                          sx={{ paddingLeft: 0, margin: 0 }}
                          primary={
                            <Typography
                              className={`children-circle ${item.children.length > 1 ? "child-list" : ""}`}
                              style={{
                                whiteSpace: "nowrap",
                                fontWeight: "400",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {t(child.translationKey)}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </NavLink>
                  </ListItem>
                ))}
              </List>
            </div>
          )}
        </Fragment>

      ))}
    </List>
  );

  const container =
    props.window !== undefined ? () => props.window().document.body : undefined;

  return (
    <div className="sidebar">
      <Box sx={{ display: "flex" }} className="sidebar-layout">
        <CssBaseline />
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            className="full-drawer"
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
              },
            }}
          >
            <Typography
              className="dashboad-logo flex justify-beetween items-center"
              component="div"
              sx={{ padding: "2rem" }}
            >
              <img
                src={logo}
                alt="finanz-logo"
                className="w-100 company-logo"
              />
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: { xs: "contents", sm: "none" } }}
              >
                <MenuIcon />
              </IconButton>
            </Typography>
            <div className="mobile-view-data">
              <div className={isAdmin ? "super-admin-users-menu" : "h-100"} style={{ overflowY: 'auto' }}>{drawer}</div>
              <div className={isAdmin ? "super-menu" : "w-100 mobile-super-admin"}>
                {isAdmin && (
                  <Typography className="super-admin w-100 h-100" component="div" sx={{ paddingBottom: "15px", height: "100%" }}>
                    <Typography component={'div'} sx={{ mx: '1rem', mb: '1rem' }}>
                      <Select
                        fullWidth
                        displayEmpty
                        sx={{
                          borderRadius: '10px',
                          fontFamily: "Poppins"
                        }}
                        value={selectClient}
                        onChange={handleClientSelect}
                        inputProps={{
                          IconComponent: () => (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "10px",
                              }}
                            >
                              <ExpandMoreIcon
                                sx={{ fontSize: "30px", color: "#33333399" }}
                              />
                            </span>
                          ),
                        }}
                      >
                        {/* Placeholder */}
                        <MenuItem value={''} sx={{ fontFamily: "Poppins" }}>
                          {t('sidebar_select_customer')}
                        </MenuItem>

                        {/* Options */}
                        {clients?.map((option, i) => (
                          <MenuItem key={i} value={option?.client?._id} sx={{ fontFamily: "Poppins" }}>
                            {`${option?.client?.firstName} ${option?.client?.lastName}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </Typography>
                    <p className="super-text w-100">SUPER ADMIN</p>
                    <List sx={{ paddingTop: "1rem" }} className="super-admin-data" >
                      {adminRoutes.map(
                        (item, index) =>
                          item.label && (
                            <Fragment key={index}>
                              <ListItem disablePadding sx={{ width: `${drawerWidth}px` }} >
                                <NavLink
                                  to={item.to ? item.to : null}
                                  onClick={(e) => {
                                    if (item.children?.length) {
                                      e.preventDefault();
                                    }
                                  }}
                                  className={({ isActive }) =>
                                    `sidebar-menu w-100 ${isActive ? "active" : ""}`
                                  }
                                >
                                  <ListItemButton
                                    onClick={() => handleToggle(index)}
                                    sx={{
                                      padding: "0 20px",
                                      color: "#000",
                                      "&:hover": {
                                        backgroundColor: "transparent",
                                        color: "transparent",
                                      },
                                    }}
                                  >
                                    <ListItemIcon className="menu-icon">
                                      <img src={item.icon} alt="menu icon" />
                                    </ListItemIcon>
                                    <ListItemText
                                      className="active-menu"
                                      primary={
                                        <Typography
                                          style={{
                                            whiteSpace: "nowrap",
                                            fontSize: "16px",
                                            fontWeight: "500",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            display: "flex",
                                            color: `${activeItem?.includes(item.to) ? "#fff" : "#000"}`,
                                            background: "none",
                                            fontFamily: "Poppins"
                                          }}
                                        >
                                          {t(item.translationKey)}

                                          {item.children && (
                                            <ExpandMoreIcon
                                              sx={{ marginLeft: "auto" }}
                                            />
                                          )}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                </NavLink>
                              </ListItem>
                              {item.children && (
                                <div
                                  className={`children-container ${activeMenu === index ? "expanded" : ""
                                    }`}
                                  style={{
                                    maxHeight:
                                      activeMenu === index
                                        ? `${item.children.length * 40}px`
                                        : "0",
                                    overflow: "hidden",
                                    transition: "max-height 0.3s ease-in-out",
                                  }}
                                >
                                  <List
                                    sx={{ paddingLeft: 0 }}
                                    className="children-menu"
                                  >
                                    {item.children.map((child, childIndex) => (
                                      <ListItem key={childIndex} disablePadding>
                                        <NavLink
                                          to={child.to}
                                          className={({ isActive }) =>
                                            `sidebar-submenu ${isActive
                                              ? "child-active"
                                              : "child-inActive"
                                            }`
                                          }
                                        >
                                          <ListItemButton
                                            sx={{
                                              padding: "0 20px",
                                              color: "#000",
                                              "&:hover": {
                                                backgroundColor: "transparent",
                                              },
                                            }}
                                          >
                                            <ListItemIcon className="menu-icon"></ListItemIcon>
                                            <ListItemText
                                              sx={{ paddingLeft: 0, margin: 0 }}
                                              primary={
                                                <Typography
                                                  className={`children-circle ${item.children.length > 1 ? "child-list" : ""}`}
                                                  style={{
                                                    whiteSpace: "nowrap",
                                                    fontWeight: "400",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    fontFamily: "Poppins"
                                                  }}
                                                >
                                                  {t(child.translationKey)}
                                                </Typography>
                                              }
                                            />
                                          </ListItemButton>
                                        </NavLink>
                                      </ListItem>
                                    ))}
                                  </List>
                                </div>
                              )}
                            </Fragment>
                          )
                      )}
                    </List>
                  </Typography>
                )}
              </div>
            </div>
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
              },
            }}
            open
          >
            <Typography
              className="dashboad-logo"
              component="div"
              sx={{ padding: "2rem", fontFamily: "Poppins" }}
            >
              <img
                src={logo}
                alt="finanz-logo"
                className="w-100 finanz-logo"
              />
            </Typography>
            <div className={isAdmin ? "data" : "customer-sidebar"}>
              {drawer}
              {isAdmin && (
                <Typography className="super-admin w-100" component="div" sx={{ fontFamily: "Poppins", paddingBottom: "15px", paddingBottom: "15px" }}>
                  <Typography component={'div'} sx={{ mx: '1rem', mb: '1rem' }}>
                    <Select
                      fullWidth
                      displayEmpty
                      sx={{
                        borderRadius: '10px',
                        fontFamily: "Poppins"
                      }}
                      value={selectClient}
                      onChange={handleClientSelect}
                      inputProps={{
                        IconComponent: () => (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              paddingRight: "10px",
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{ fontSize: "30px", color: "#33333399" }}
                            />
                          </span>
                        ),
                      }}
                    >
                      {/* Placeholder */}
                      <MenuItem value={''} sx={{ fontFamily: "Poppins" }}>
                       {t('sidebar_select_customer')}
                      </MenuItem>

                      {/* Options */}
                      {clients?.map((option, i) => (
                        <MenuItem key={i} value={option?.client?._id} sx={{ fontFamily: "Poppins" }}>
                          {`${option?.client?.firstName} ${option?.client?.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </Typography>
                  <p className="super-text">SUPER ADMIN</p>
                  <List sx={{ paddingTop: "1rem" }} className="super-admin-data">
                    {adminRoutes.map(
                      (item, index) =>
                        item.label && (
                          <Fragment key={index}>
                            <ListItem disablePadding sx={{ width: "100%" }}>
                              <NavLink
                                to={item.to ? item.to : null}
                                onClick={(e) => {
                                  if (item.children?.length) {
                                    e.preventDefault();
                                  }
                                }}
                                className={({ isActive }) =>
                                  `sidebar-menu ${isActive ? "active" : ""}`
                                }
                              >
                                <ListItemButton
                                  onClick={() => handleToggle(index)}
                                  sx={{
                                    padding: "0 20px",
                                    color: "#000",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                      color: "transparent",
                                    },
                                  }}
                                >
                                  <ListItemIcon className="menu-icon">
                                    <img src={item.icon} alt="menu icon" />
                                  </ListItemIcon>
                                  <ListItemText
                                    className="active-menu"
                                    primary={
                                      <Typography
                                        style={{
                                          whiteSpace: "nowrap",
                                          fontSize: "16px",
                                          fontWeight: "500",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "flex",
                                          background: "none",
                                          fontFamily: "Poppins",
                                          color: `${activeItem?.includes(item.to) ? "#fff" : "#000"}`,
                                        }}
                                      >
                                        {t(item.translationKey)}

                                        {item.children && (
                                          <ExpandMoreIcon
                                            sx={{ marginLeft: "auto" }}
                                          />
                                        )}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              </NavLink>
                            </ListItem>
                            {item.children && (
                              <div
                                className={`children-container ${activeMenu === index ? "expanded" : ""
                                  }`}
                                style={{
                                  maxHeight:
                                    activeMenu === index
                                      ? `${item.children.length * 40}px`
                                      : "0",
                                  overflow: "hidden",
                                  transition: "max-height 0.3s ease-in-out",
                                }}
                              >
                                <List
                                  sx={{ paddingLeft: 0 }}
                                  className="children-menu"
                                >
                                  {item.children.map((child, childIndex) => (
                                    <ListItem key={childIndex} disablePadding>
                                      <NavLink
                                        to={child.to}
                                        className={({ isActive }) =>
                                          `sidebar-submenu ${isActive
                                            ? "child-active"
                                            : "child-inActive"
                                          }`
                                        }
                                      >
                                        <ListItemButton
                                          sx={{
                                            padding: "0 20px",
                                            color: "#000",
                                            "&:hover": {
                                              backgroundColor: "transparent",
                                            },
                                          }}
                                        >
                                          <ListItemIcon className="menu-icon"></ListItemIcon>
                                          <ListItemText
                                            sx={{ paddingLeft: 0, margin: 0 }}
                                            primary={
                                              <Typography
                                                className={`children-circle ${item.children.length > 1 ? "child-list" : ""}`}
                                                style={{
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "400",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  fontFamily: "Poppins"
                                                }}
                                              >
                                                {t(child.translationKey)}
                                              </Typography>
                                            }
                                          />
                                        </ListItemButton>
                                      </NavLink>
                                    </ListItem>
                                  ))}
                                </List>
                              </div>
                            )}
                          </Fragment>
                        )
                    )}
                  </List>
                </Typography>
              )}
            </div>
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{ flexGrow: 1, p: 3, width: { sm: "200px" }, overflow: "auto" }}
          className="wrapper"
        >
          <div className="navbar-text">
            <NavbarComponent
              drawerWidth={drawerWidth}
              handleDrawerToggle={handleDrawerToggle}
            />
            <Typography sx={{ padding: "20px" }}>
              {/* <Outlet /> */}{props.children}
            </Typography>
          </div>
        </Box>
      </Box >
    </div >
  );
};

export default Sidebar;
